"use client";

import { useId, useState, type FormEvent } from "react";
import { useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import type { PUBLIC_REPORTED_PRICE_SOURCES } from "@convex/constants/reportedPrices";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { trackToolUsed } from "@/lib/analytics/tool-events";
import { describeError } from "@/lib/handle-error";
import { checkablePostalCode, type CheckSide } from "./price-check-model";

export type PriceReportSource = (typeof PUBLIC_REPORTED_PRICE_SOURCES)[number];

export type PriceReport = {
  productTypeSlug: string;
  condition: string;
  sizeKey: string;
  priceCents: number;
  side: CheckSide;
  postalCode: string;
  stateCode: string | null;
};

type Submission = { reportKey: string; confirmationSent: boolean };

const SUBMIT_FAILED = "Couldn't add your price. Try again.";

function reportArgs(report: PriceReport, source: PriceReportSource, email: string, website: string) {
  const postalCode = checkablePostalCode(report.postalCode);
  const stateCode = postalCode && report.stateCode ? report.stateCode : undefined;
  return {
    productTypeSlug: report.productTypeSlug,
    condition: report.condition,
    sizeKey: report.sizeKey || undefined,
    priceCents: report.priceCents,
    basis: "pickup" as const,
    side: report.side,
    postalCode: stateCode ? postalCode : undefined,
    stateCode,
    source,
    email: email.trim() || undefined,
    website: website || undefined,
  };
}

function ReportedNotice({ confirmationSent }: { confirmationSent: boolean }) {
  return (
    <p role="status" className="text-sm text-foreground">
      <span className="font-semibold">Thanks, your price is in.</span>{" "}
      {confirmationSent ? "Open the link we emailed you to confirm it." : null}
    </p>
  );
}

function HoneypotField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const inputId = useId();
  return (
    <div className="absolute -left-[9999px] opacity-0" aria-hidden="true">
      <label htmlFor={inputId}>Website</label>
      <input
        id={inputId}
        type="text"
        name="website"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        tabIndex={-1}
        autoComplete="off"
      />
    </div>
  );
}

function useReportSubmission(report: PriceReport, source: PriceReportSource) {
  const submit = useMutation(api.reportedPrices.submit.submit);
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const reportKey = JSON.stringify(report);
  const send = async (email: string, website: string) => {
    setIsPending(true);
    setError(null);
    try {
      const saved = await submit(reportArgs(report, source, email, website));
      setSubmission({ reportKey, confirmationSent: saved.confirmationSent });
      const productTypeSlug = report.productTypeSlug;
      trackToolUsed({ tool: "price-check", tool_step: "reported", productTypeSlug });
      if (email.trim()) {
        trackToolUsed({ tool: "price-check", tool_step: "email_submitted", productTypeSlug });
      }
    } catch (failure) {
      setError(describeError(failure, SUBMIT_FAILED));
    } finally {
      setIsPending(false);
    }
  };
  const sent = submission?.reportKey === reportKey ? submission : null;
  return { sent, error, isPending, send };
}

export function PriceReportForm({
  report,
  source,
}: {
  report: PriceReport;
  source: PriceReportSource;
}) {
  const emailId = useId();
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const { sent, error, isPending, send } = useReportSubmission(report, source);

  if (sent) return <ReportedNotice confirmationSent={sent.confirmationSent} />;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void send(email, website);
  };

  return (
    <form onSubmit={onSubmit} noValidate className="relative grid gap-3">
      <Field>
        <FieldLabel htmlFor={emailId}>Email (optional)</FieldLabel>
        <Input
          id={emailId}
          type="email"
          autoComplete="email"
          placeholder="you@company.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <FieldDescription>
          Add it and we send one link to confirm your price.
        </FieldDescription>
      </Field>
      <HoneypotField value={website} onChange={setWebsite} />
      <Button type="submit" variant="outline" disabled={isPending}>
        {isPending ? "Adding…" : "Add my price to the index"}
      </Button>
      {error && <FieldError>{error}</FieldError>}
    </form>
  );
}
